import { InProductTrialsOverlayWindowType } from '../../../types/InProductTrialsOverlayWindowType';
import {
  InProductTrialsOverlayWindowButton,
  InProductTrialsOverlayWindowContentConfig,
} from '../../types/InProductTrialsOverlayWindowContentConfig';
import { BaseWebComponentTranslator } from '../../../../../../framework/BaseWebComponentTranslator';
import { ButtonColors, ButtonThemes, ButtonTypes } from '../../../../../../ui/gzButton/types';
import { InProductTrialsOverlayWindowEventType } from '../../events/InProductTrialsOverlayWindowEventType';
import { InProductTrialTypeEnum } from '@nirvana/shared/dtos/types/in-product-trials';
import { CoolDownPeriodMonths } from '../../../../types/InProductTrialCannotBeStartedReason';
import { getTranslatedProductsHubDescriptionText } from '../../../../utils/utils';
import { getTranslatedInProductTrialType } from '../../../../utils/InProductTrialType';

// get paragraph
function getParagraph(text: string): string {
  return `<p>${text}</p>`;
}

function getInProductTrialsOverlayWindowContentConfig(): {
  [type in InProductTrialsOverlayWindowType]: InProductTrialsOverlayWindowContentConfig;
} {
  const translator = BaseWebComponentTranslator.getInstance();
  const imageSrcReadyToTry = 'app/webcomponents/assets/images/trial/banners/readyToTry/gz-overlay-ready-to-try.png';
  const imageSrcTrialEnded = 'app/webcomponents/assets/images/trial/banners/trialEnded/gz-overlay-trial-ended.png';

  const twoButtonsSettings: {
    button1: InProductTrialsOverlayWindowButton;
    button2: InProductTrialsOverlayWindowButton;
  } = {
    button1: {
      innerText: translator.translateFn('Learn more'),
      theme: ButtonThemes.DARK,
      color: ButtonColors.PRIMARY,
      type: ButtonTypes.FULL,
      event: InProductTrialsOverlayWindowEventType.LearnMore,
    },
    button2: {
      innerText: translator.translateFn('Dismiss'),
      theme: ButtonThemes.DARK,
      color: ButtonColors.PRIMARY,
      type: ButtonTypes.QUIET,
      event: InProductTrialsOverlayWindowEventType.Dismiss,
    },
  };

  const threeButtonsSettingsAddModules: {
    button1: InProductTrialsOverlayWindowButton;
    button2: InProductTrialsOverlayWindowButton;
    button3: InProductTrialsOverlayWindowButton;
  } = {
    button1: {
      innerText: translator.translateFn('Add new modules'),
      theme: ButtonThemes.DARK,
      color: ButtonColors.PRIMARY,
      type: ButtonTypes.FULL,
      event: InProductTrialsOverlayWindowEventType.AddModules,
    },
    button2: {
      innerText: translator.translateFn('Learn more'),
      theme: ButtonThemes.DARK,
      color: ButtonColors.WHITE,
      type: ButtonTypes.OUTLINE,
      event: InProductTrialsOverlayWindowEventType.LearnMore,
    },
    button3: {
      innerText: translator.translateFn('Dismiss'),
      theme: ButtonThemes.DARK,
      color: ButtonColors.WHITE,
      type: ButtonTypes.OUTLINE,
      event: InProductTrialsOverlayWindowEventType.Dismiss,
    },
  };

  const threeButtonsSettingsRemoveModules: {
    button1: InProductTrialsOverlayWindowButton;
    button2: InProductTrialsOverlayWindowButton;
    button3: InProductTrialsOverlayWindowButton;
  } = {
    button1: {
      innerText: translator.translateFn('Remove modules'),
      theme: ButtonThemes.DARK,
      color: ButtonColors.PRIMARY,
      type: ButtonTypes.FULL,
      event: InProductTrialsOverlayWindowEventType.RemoveModules,
    },
    button2: {
      innerText: translator.translateFn('Learn more'),
      theme: ButtonThemes.DARK,
      color: ButtonColors.WHITE,
      type: ButtonTypes.OUTLINE,
      event: InProductTrialsOverlayWindowEventType.LearnMore,
    },
    button3: {
      innerText: translator.translateFn('Dismiss'),
      theme: ButtonThemes.DARK,
      color: ButtonColors.WHITE,
      type: ButtonTypes.OUTLINE,
      event: InProductTrialsOverlayWindowEventType.Dismiss,
    },
  };

  // common xdr
  const commonReadyToTryOverlayWindowXdr = {
    title: translator.translateFn('Empower your organization to detect and swiftly respond to threats'),
    text: getParagraph(
      translator.translateFn(
        'Deploy and configure XDR and start testing its detection, response, and prevention features. XDR integrates, correlates and contextualizes data from various sources across your organization to provide easy identification and mitigation of security threats.'
      )
    ),
  };

  return {
    [InProductTrialsOverlayWindowType.ProductTrialsHubNewSection]: {
      imageSrc: 'app/webcomponents/assets/images/trial/gz-overlay-header.png',
      title: translator.translateFn('Experience the next tier of GravityZone security'),
      text: getParagraph(getTranslatedProductsHubDescriptionText()),
      buttons: twoButtonsSettings,
    },
    [InProductTrialsOverlayWindowType.OfficeSecurityPackReadyToTry]: {
      imageSrc: imageSrcReadyToTry,
      title: translator.translateFn('Diversify your security portfolio with new features'),
      text:
        getParagraph(
          translator.translateFn(
            'Enhance your network protection and implement safety measures tailored to your needs. Assess, analyze, and resolve endpoints weaknesses using data based on a vast number of indicators of risk.'
          )
        ) +
        getParagraph(
          translator.translateFn(
            'Control external device usage and prevent sensitive data leakage and malware infections.'
          )
        ),
      buttons: threeButtonsSettingsAddModules,
    },
    [InProductTrialsOverlayWindowType.NextGenIncidentsReadyToTry]: {
      imageSrc: imageSrcReadyToTry,
      title: translator.translateFn('Accelerate incident response with your new Incidents section'),
      text: getParagraph(
        translator.translateFn(
          'Take swift and decisive action against ongoing incidents and future threats. Explore the Incidents section for detailed Endpoint Incident and Detected Threat information. Powered by automated Root Cause Analysis and attack visualizations, your team identifies points of entry, stops attacks, and hardens endpoints at the click of a button.'
        )
      ),
      buttons: twoButtonsSettings,
    },
    [InProductTrialsOverlayWindowType.NextGenWithReconfigureReadyToTry]: {
      imageSrc: imageSrcReadyToTry,
      title: translator.translateFn('Diversify your security portfolio with new tools and features'),
      text:
        getParagraph(
          translator.translateFn(
            'Enhance your network protection and implement safety measures tailored to your needs. Assess, analyze, and resolve endpoints weaknesses using data based on a vast number of indicators of risk.'
          )
        ) +
        getParagraph(
          translator.translateFn(
            'Control external device usage and prevent sensitive data leakage and malware infections.'
          )
        ) +
        getParagraph(
          translator.translateFn(
            'Employ a powerful layer of protection against advanced threats by performing automatic and in-depth analysis of suspicious files.'
          )
        ),
      buttons: threeButtonsSettingsAddModules,
    },
    [InProductTrialsOverlayWindowType.AtdIncidentsReadyToTry]: {
      imageSrc: imageSrcReadyToTry,
      title: translator.translateFn('Boost your investigations with extended visibility into your environment'),
      text: getParagraph(
        translator.translateFn(
          'Close investigations faster with correlated data from across your managed endpoints. Your Incidents page now features the Extended Incidents section. This includes enhanced attack and threat data, which helps to detect early-stage attacks. Additionally, it enables the identification of lateral spreads and effective response throughout your environment.'
        )
      ),
      buttons: threeButtonsSettingsAddModules,
    },
    [InProductTrialsOverlayWindowType.PatchManagementNetworkReadyToTry]: {
      imageSrc: imageSrcReadyToTry,
      title: translator.translateFn('Reduce exploit risks and security overhead with {productName}', {
        productName: getTranslatedInProductTrialType(InProductTrialTypeEnum.PatchManagement),
      }),
      text: getParagraph(
        translator.translateFn(
          'You are now ready to deploy and configure {productName}. After adding the new module on the endpoint, you will be able to test capabilities such as patch scanning, and automated or manual patch deployment.',
          {
            productName: getTranslatedInProductTrialType(InProductTrialTypeEnum.PatchManagement),
          }
        )
      ),
      buttons: threeButtonsSettingsAddModules,
    },
    [InProductTrialsOverlayWindowType.VolumeEncryptionNetworkReadyToTry]: {
      imageSrc: imageSrcReadyToTry,
      title: translator.translateFn('Protect data and simplify compliance with {productName}', {
        productName: getTranslatedInProductTrialType(InProductTrialTypeEnum.VolumeEncryption),
      }),
      text: getParagraph(
        translator.translateFn(
          'You are now ready to deploy and configure {productName}. After adding the new module on the endpoint you will be able to test capabilities such as encrypting or decrypting volumes, recovering keys or generating encryption reports.',
          {
            productName: getTranslatedInProductTrialType(InProductTrialTypeEnum.VolumeEncryption),
          }
        )
      ),
      buttons: threeButtonsSettingsAddModules,
    },
    [InProductTrialsOverlayWindowType.MobileSecurityMobileReadyToTry]: {
      imageSrc: imageSrcReadyToTry,
      title: translator.translateFn('Protect your mobile devices with {productName}', {
        productName: getTranslatedInProductTrialType(InProductTrialTypeEnum.MobileSecurity),
      }),
      text: getParagraph(
        translator.translateFn(
          "You're now ready to deploy and configure mobile security. After getting set up, you will be able to test capabilities such as advanced malware detection, network protection, anti-phishing, and protection against web attacks."
        )
      ),
      buttons: twoButtonsSettings,
    },
    [InProductTrialsOverlayWindowType.IntegrityMonitorReadyToTry]: {
      imageSrc: imageSrcReadyToTry,
      title: translator.translateFn('Monitor and set up rules for files in your network using {productName}', {
        productName: getTranslatedInProductTrialType(InProductTrialTypeEnum.IntegrityMonitor),
      }),
      text: getParagraph(
        translator.translateFn(
          'You are now ready to deploy and configure Integrity Monitoring. After adding the new module on the endpoint agent you will be able to test capabilities such as monitoring file changes, identifying configuration changes in real-time, and setting up rules that apply specific actions on your files.'
        )
      ),
      buttons: threeButtonsSettingsAddModules,
    },
    [InProductTrialsOverlayWindowType.ContainerProtectionReadyToTry]: {
      imageSrc: imageSrcReadyToTry,
      title: translator.translateFn('Protect your containers and their hosts with {productName}', {
        productName: getTranslatedInProductTrialType(InProductTrialTypeEnum.ContainerProtection),
      }),
      text: getParagraph(
        translator.translateFn(
          'You are now ready to deploy and configure Container Security. After adding the new module on a Linux endpoint agent or deploying a Security Container, you will be able to monitor and protect containers and their hosts with Antimalware On-Access scanning, EDR Sensors, and Advanced Anti-Exploit.'
        )
      ),
      buttons: twoButtonsSettings,
    },
    [InProductTrialsOverlayWindowType.StorageProtectionReadyToTry]: {
      imageSrc: imageSrcReadyToTry,
      title: translator.translateFn(
        'Secure your prominent network-storage and file-sharing systems using {productName}',
        {
          productName: getTranslatedInProductTrialType(InProductTrialTypeEnum.StorageProtection),
        }
      ),
      text: getParagraph(
        translator.translateFn(
          'You are now ready to deploy and configure Storage Protection. After adding at least two Security Servers and configuring the integration with you storage solution, you will be able to test the machine learning-based protection for you content sharing systems.'
        )
      ),
      buttons: twoButtonsSettings,
    },
    [InProductTrialsOverlayWindowType.XdrFullReadyToTry]: {
      imageSrc: imageSrcReadyToTry,
      title: commonReadyToTryOverlayWindowXdr.title,
      text: commonReadyToTryOverlayWindowXdr.text,
      buttons: threeButtonsSettingsAddModules,
    },
    [InProductTrialsOverlayWindowType.XdrSensorsReadyToTry]: {
      imageSrc: imageSrcReadyToTry,
      title: commonReadyToTryOverlayWindowXdr.title,
      text: commonReadyToTryOverlayWindowXdr.text,
      buttons: twoButtonsSettings,
    },
    [InProductTrialsOverlayWindowType.NadAndEraReadyToTry]: {
      imageSrc: imageSrcReadyToTry,
      title: translator.translateFn('See the Risks, Stop the Threats - All in One Powerful Add-On'),
      text: getParagraph(
        translator.translateFn(
          "You're now ready to deploy and configure Network Attack Defense and Risk Management Add-on. After adding the new module on the endpoint, you will be able to stop network-based attack (brute force attacks, port scans, password stealers, lateral movement), identify and mitigate risks across endpoints, applications and user behavior."
        )
      ),
      buttons: threeButtonsSettingsAddModules,
    },
    [InProductTrialsOverlayWindowType.WebAccessAndDeviceControlReadyToTry]: {
      imageSrc: imageSrcReadyToTry,
      title: translator.translateFn('From Devices to Websites - Stay in Control, Stay Secure'),
      text: getParagraph(
        translator.translateFn(
          "You're now ready to deploy and configure Web Access and Device Control Add-on. After adding the new module on the endpoint, you will be able to restrict user access to certain applications, sites or web categories, prevent unauthorized use of external devices such as USB drives, and support compliance with relevant regulations."
        )
      ),
      buttons: threeButtonsSettingsAddModules,
    },


    // trial ended
    [InProductTrialsOverlayWindowType.OfficeSecurityPackTrialEnded]: {
      imageSrc: imageSrcTrialEnded,
      title: translator.translateFn('Thank you for your interest in {productName}! Your trial has ended.', {
        productName: getTranslatedInProductTrialType(InProductTrialTypeEnum.OfficeSecurityPack),
      }),
      text: getParagraph(
        translator.translateFn(
          'All licensing changes have been reverted. The modules deployed to support the trialed products are now inactive and any additional features added by activating the trial are no longer available. To remove the inactive modules from your endpoints click the Remove modules button.'
        )
      ),
      buttons: threeButtonsSettingsRemoveModules,
    },
    [InProductTrialsOverlayWindowType.NextGenTrialEnded]: {
      imageSrc: imageSrcTrialEnded,
      title: translator.translateFn('Thank you for your interest in {productName}! Your trial has ended.', {
        productName: getTranslatedInProductTrialType(InProductTrialTypeEnum.NextGen),
      }),
      text:
        getParagraph(translator.translateFn('All licensing changes have been reverted.')) +
        getParagraph(
          translator.translateFn(
            'Any additional features added by the trial are no longer available. You will be able to try this product again in {coolDownPeriodMonths} months. If you require additional information or would like to enroll in another trial sooner, please contact our Enterprise Support team.',
            {
              coolDownPeriodMonths: CoolDownPeriodMonths,
            }
          )
        ),
      buttons: twoButtonsSettings,
    },
    [InProductTrialsOverlayWindowType.NextGenWithReconfigureTrialEnded]: {
      imageSrc: imageSrcTrialEnded,
      title: translator.translateFn('Thank you for your interest in {productName}! Your trial has ended.', {
        productName: getTranslatedInProductTrialType(InProductTrialTypeEnum.NextGenWithReconfigurePopup),
      }),
      text: getParagraph(
        translator.translateFn(
          'All licensing changes have been reverted. The modules deployed to support the trialed products are now inactive and any additional features added by activating the trial are no longer available. To remove the inactive modules from your endpoints click the Remove modules button.'
        )
      ),
      buttons: threeButtonsSettingsRemoveModules,
    },
    [InProductTrialsOverlayWindowType.AtdTrialEnded]: {
      imageSrc: imageSrcTrialEnded,
      title: translator.translateFn('Thank you for your interest in {productName}! Your trial has ended.', {
        productName: getTranslatedInProductTrialType(InProductTrialTypeEnum.Atd),
      }),
      text:
        getParagraph(
          translator.translateFn(
            'All licensing changes have been reverted. The modules deployed for supporting the new products are now inactive and any additional features added by the trial are no longer available. To remove the inactive modules from your endpoints, click on the {bold}Remove modules{/bold} button.',
            {
              bold: '<strong>',
              '/bold': '</strong>',
            }
          )
        ) +
        getParagraph(
          translator.translateFn(
            'You will be able to try this product again in {coolDownPeriodMonths} months. If you require additional information or would like to enroll in another trial sooner, please contact our Enterprise Support team.',
            {
              coolDownPeriodMonths: CoolDownPeriodMonths,
            }
          )
        ),
      buttons: threeButtonsSettingsRemoveModules,
    },
    [InProductTrialsOverlayWindowType.PatchManagementTrialEnded]: {
      imageSrc: imageSrcTrialEnded,
      title: translator.translateFn('Thank you for your interest in {productName}! Your trial has ended.', {
        productName: getTranslatedInProductTrialType(InProductTrialTypeEnum.PatchManagement),
      }),
      text: getParagraph(
        translator.translateFn(
          'All licensing changes have been reverted. The modules deployed to support the trial products are now inactive and any additional features added by activating the trial are no longer available. To remove the inactive modules from your endpoints, click the {bold}Remove modules{/bold} button.',
          {
            bold: '<strong>',
            '/bold': '</strong>',
          }
        )
      ),
      buttons: threeButtonsSettingsRemoveModules,
    },
    [InProductTrialsOverlayWindowType.VolumeEncryptionTrialEnded]: {
      imageSrc: imageSrcTrialEnded,
      title: translator.translateFn('Thank you for your interest in {productName}! Your trial has ended.', {
        productName: getTranslatedInProductTrialType(InProductTrialTypeEnum.VolumeEncryption),
      }),
      text: getParagraph(
        translator.translateFn(
          'All licensing changes have been reverted. The modules deployed to support the trial products are now inactive and any additional features added by activating the trial are no longer available. To remove the inactive modules from your endpoints, click the {bold}Remove modules{/bold} button.',
          {
            bold: '<strong>',
            '/bold': '</strong>',
          }
        )
      ),
      buttons: threeButtonsSettingsRemoveModules,
    },
    [InProductTrialsOverlayWindowType.MobileSecurityTrialEnded]: {
      imageSrc: imageSrcTrialEnded,
      title: translator.translateFn('Thank you for your interest in {productName}! Your trial has ended.', {
        productName: getTranslatedInProductTrialType(InProductTrialTypeEnum.MobileSecurity),
      }),
      text: getParagraph(
        translator.translateFn(
          'All licensing changes have been reverted. The mobile security capabilities activated during the trial are no longer available.'
        )
      ),
      buttons: twoButtonsSettings,
    },
    [InProductTrialsOverlayWindowType.IntegrityMonitorTrialEnded]: {
      imageSrc: imageSrcTrialEnded,
      title: translator.translateFn('Thank you for your interest in {productName}! Your trial has ended.', {
        productName: getTranslatedInProductTrialType(InProductTrialTypeEnum.IntegrityMonitor),
      }),
      text: getParagraph(
        translator.translateFn(
          'All licensing changes have been reverted. The modules deployed to support the trialed products are now inactive and any additional features added by activating the trial are no longer available. To remove the inactive modules from your endpoints click the Remove modules button.'
        )
      ),
      buttons: threeButtonsSettingsRemoveModules,
    },
    [InProductTrialsOverlayWindowType.ContainerProtectionTrialEnded]: {
      imageSrc: imageSrcTrialEnded,
      title: translator.translateFn('Thank you for your interest in {productName}! Your trial has ended.', {
        productName: getTranslatedInProductTrialType(InProductTrialTypeEnum.ContainerProtection),
      }),
      text: getParagraph(
        translator.translateFn(
          'All licensing changes have been reverted. The modules deployed to support the trialed products are now inactive and any additional features added by activating the trial are no longer available. To remove the inactive modules from your endpoints click the Remove modules button.'
        )
      ),
      buttons: threeButtonsSettingsRemoveModules,
    },
    [InProductTrialsOverlayWindowType.StorageProtectionTrialEnded]: {
      imageSrc: imageSrcTrialEnded,
      title: translator.translateFn('Thank you for your interest in {productName}! Your trial has ended.', {
        productName: getTranslatedInProductTrialType(InProductTrialTypeEnum.StorageProtection),
      }),
      text: getParagraph(
        translator.translateFn(
          'All licensing changes have been reverted. The Security Servers deployed to support the trialed products are now inactive and any additional features added by activating the trial are no longer available.'
        )
      ),
      buttons: twoButtonsSettings,
    },
    [InProductTrialsOverlayWindowType.XdrFullTrialEnded]: {
      imageSrc: imageSrcTrialEnded,
      title: translator.translateFn('Thank you for your interest in {productName}! Your trial has ended.', {
        productName: getTranslatedInProductTrialType(InProductTrialTypeEnum.XdrFull),
      }),
      text: getParagraph(
        translator.translateFn(
          'All licensing changes have been reverted. The module and the sensors deployed for supporting the new products are now inactive and any additional features added by the trial are no longer available. To remove the inactive modules from your endpoints, click on the {bold}Remove modules{/bold} button.',
          {
            bold: '<strong>',
            '/bold': '</strong>',
          }
        )
      ),
      buttons: threeButtonsSettingsRemoveModules,
    },
    [InProductTrialsOverlayWindowType.XdrSensorsTrialEnded]: {
      imageSrc: imageSrcTrialEnded,
      title: translator.translateFn('Thank you for your interest in {productName}! Your trial has ended.', {
        productName: getTranslatedInProductTrialType(InProductTrialTypeEnum.XdrSensors),
      }),
      text: getParagraph(
        translator.translateFn(
          'All licensing changes have been reverted. The new sensor types deployed are now inactive and any additional features added by the trial are no longer available.'
        )
      ),
      buttons: twoButtonsSettings,
    },
    [InProductTrialsOverlayWindowType.NadAndEraTrialEnded]: {
      imageSrc: imageSrcTrialEnded,
      title: translator.translateFn('Thank you for your interest in {productName}! Your trial has ended.', {
        productName: getTranslatedInProductTrialType(InProductTrialTypeEnum.NadAndEraAddon),
      }),
      text: getParagraph(
        translator.translateFn(
          'All licensing changes have been reverted. The modules deployed to support the trialed products are now inactive and any additional features added by activating the trial are no longer available. To remove the inactive modules from your endpoints click the Remove modules button.'
        )
      ),
      buttons: threeButtonsSettingsRemoveModules,
    },
    [InProductTrialsOverlayWindowType.WebAccessAndDeviceControlTrialEnded]: {
      imageSrc: imageSrcTrialEnded,
      title: translator.translateFn('Thank you for your interest in {productName}! Your trial has ended.', {
        productName: getTranslatedInProductTrialType(InProductTrialTypeEnum.WebAccessAndDeviceControlAddon),
      }),
      text: getParagraph(
        translator.translateFn(
          'All licensing changes have been reverted. The modules deployed to support the trialed products are now inactive and any additional features added by activating the trial are no longer available. To remove the inactive modules from your endpoints click the Remove modules button.'
        )
      ),
      buttons: threeButtonsSettingsRemoveModules,
    },
  };
}

export function getContentConfigForInProductTrialsOverlayWindowType(
  overlayWindowType: InProductTrialsOverlayWindowType
): InProductTrialsOverlayWindowContentConfig {
  return getInProductTrialsOverlayWindowContentConfig()[overlayWindowType];
}
